import type {Relation} from "typeorm";
import {Column, Entity, ManyToMany, ManyToOne, OneToMany} from "typeorm";
import {BaseEntity} from "@/modules/base/entity/BaseEntity";
import {Length, MaxLength, Min} from "class-validator";
import {Player} from "@/modules/team/entity";
import {StaffMember} from "@/modules/staff/entity";
import {YouthPlayer} from "@/modules/youth/entity";
import {Division} from "@/modules/competitions/entity";
import {Negotiation} from "@/modules/diplomacy/entity";
import {FormationEnum} from "@/modules/team/enums";
import {Country} from "@/modules/base/entity";
import {CanNegotiateInterface} from "@/modules/diplomacy/interfaces";
import {AIQualityLevelEnum} from "@/modules/ai/enums";

@Entity()
export class Team extends BaseEntity implements CanNegotiateInterface {
  @ManyToOne(() => Country, {eager: true})
  country: Relation<Country>;

  @Column({type: 'varchar', length: 50})
  @MaxLength(50)
  name: string;

  @Column({type: 'varchar', length: 15})
  @MaxLength(15)
  shortName: string;

  @Column({type: 'varchar', length: 7})
  @Length(7)
  primaryColor: string;

  @Column({type: 'varchar', length: 7})
  @Length(7)
  secondaryColor: string;

  @Column({type: 'float', unsigned: true})
  @Min(1)
  level: number;

  @Column({type: 'integer', unsigned: true})
  @Min(1)
  prestige = 100;

  @Column({type: 'integer', unsigned: true})
  @Min(0)
  staffSlots = 1;

  @Column({type: 'boolean'})
  user = false;

  @OneToMany(() => Player, (Player) => Player.team)
  players: Relation<Player[]>;

  @ManyToMany(() => Division, division => division.teams)
  divisions: Relation<Division[]>;

  @OneToMany(() => StaffMember, staffMember => staffMember.team, {eager: true})
  staffMembers: Relation<StaffMember[]>;

  @OneToMany(() => YouthPlayer, youthPlayer => youthPlayer.team)
  youthPlayers: Relation<YouthPlayer[]>;

  @OneToMany(() => YouthPlayer, youthPlayer => youthPlayer.recruitedBy)
  recruitedYouthPlayers: Relation<YouthPlayer[]>;

  @OneToMany(() => Negotiation, negotiation => negotiation.team)
  negotiations: Relation<Negotiation[]>;

  @Column({type: 'integer', unsigned: true})
  @Min(0)
  patience: number;

  @Column({type: 'integer', unsigned: true})
  @Min(0)
  greediness: number;

  @Column({type: 'integer', unsigned: true})
  @Min(0)
  resentfulness: number;

  @Column({type: 'integer'})
  balance = 0;

  @Column({type: "varchar", enum: FormationEnum})
  formation = FormationEnum.F_433;

  @Column({type: "varchar", enum: AIQualityLevelEnum})
  aiQualityLevel: AIQualityLevelEnum;

  @Column({type: 'float'})
  @Min(0)
  actionPointsScouting = 25;

  @Column({type: 'float'})
  @Min(0)
  actionPointsNegotiating = 25;

  @Column({type: 'float'})
  @Min(0)
  actionPointsTrainingGK = 25;

  @Column({type: 'float'})
  @Min(0)
  actionPointsTrainingDF = 25;

  @Column({type: 'float'})
  @Min(0)
  actionPointsTrainingMF = 25;

  @Column({type: 'float'})
  @Min(0)
  actionPointsTrainingAT = 25;

  @Column({type: 'float'})
  @Min(0)
  actionPointsCoins = 0;

  get actionPointsTraining(): number {
    return collect([
      this.actionPointsTrainingGK,
      this.actionPointsTrainingDF,
      this.actionPointsTrainingMF,
      this.actionPointsTrainingAT,
    ])
      .sum() as number;
  }

  get isCurrent() {
    return this.user;
  }

  get image(): string {
    const index = this.user ? 4 : (this.id % 3 + 1);

    return `/logos/logo-${index}.png`;
  }

  static currentInstance?: Team;

  static async current(): Promise<Team> {
    // if (!this.currentInstance) {
    this.currentInstance = await this
      .createQueryBuilder('team')
      .where('team.user = true')
      .getOneOrFail();
    // }

    return this.currentInstance;
  }
}
